import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ChevronLeft, ChevronRight, BookOpen, Share2 } from "lucide-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { fetchChapter, fetchSettings } from "@/lib/api";
import { parseContent, splitSlug } from "@/lib/utils";
import { AdSlot, CustomAdSlot } from "@/components/ad-slot";
import { lazy, Suspense, useState, useEffect } from "react";
const CommentSection = lazy(() =>
  import("@/components/comment-section").then((m) => ({ default: m.CommentSection })),
);

export const Route = createFileRoute(
  "/novel/$id/$slug/chapter/$chapterId/$chapterSlug",
)({
  loader: async ({ params, context: { queryClient } }) => {
    try {
      const fullNovelSlug = params.id === "p" ? params.slug : `${params.slug}-${params.id}`;
      const fullChapterSlug = params.chapterId === "p" ? params.chapterSlug : `${params.chapterSlug}-${params.chapterId}`;
      const [chapter, settings] = await Promise.all([
        queryClient.ensureQueryData({
          queryKey: ["chapter", fullNovelSlug, fullChapterSlug],
          queryFn: () => fetchChapter(fullNovelSlug, fullChapterSlug),
        }),
        queryClient.ensureQueryData({
          queryKey: ["settings"],
          queryFn: fetchSettings,
        }),
      ]);
      return { chapter, settings };
    } catch (e) {
      throw notFound();
    }
  },
  head: ({ loaderData, params }) => {
    const data = loaderData as any;
    const settings = data?.settings;
    const c = data?.chapter;
    const siteName = settings?.site_name || "Lihar Aksara";
    if (!c) return { meta: [{ title: `Tidak ditemukan — ${siteName}` }] };

    const title = c.metaTitle || `Bab ${c.order}: ${c.title} — ${c.post?.title}`;
    const cleanContent = c.content.replace(/<[^>]*>/g, "").substring(0, 160);
    const description =
      c.metaDescription || (cleanContent.length > 157 ? cleanContent + "..." : cleanContent);

    const canonicalUrl = `https://liharaksara.web.id/novel/${params.id}/${params.slug}/chapter/${params.chapterId}/${params.chapterSlug}`;
    const novelUrl = `https://liharaksara.web.id/novel/${params.id}/${params.slug}`;

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": title,
      "description": description,
      "publisher": {
        "@type": "Organization",
        "name": siteName
      }
    };

    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://liharaksara.web.id"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Novel",
          "item": "https://liharaksara.web.id/novel"
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": c.post?.title,
          "item": novelUrl
        },
        {
          "@type": "ListItem",
          "position": 4,
          "name": `Bab ${c.order}: ${c.title}`,
          "item": canonicalUrl
        }
      ]
    };

    return {
      meta: [
        { title: `${title} — ${siteName}` },
        { name: "description", content: description },
        { name: "keywords", content: `${c.post?.title}, bab ${c.order}, ${c.title}, novel` },
        { property: "og:title", content: `${title} — ${siteName}` },
        { property: "og:description", content: description },
        { property: "og:url", content: canonicalUrl },
        { property: "og:type", content: "article" },
        { property: "og:site_name", content: siteName },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: canonicalUrl }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(jsonLd),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbLd),
        },
      ],
    };
  },
  component: ChapterPage,
});

function ChapterPage() {
  const { id, slug, chapterId, chapterSlug } = Route.useParams();
  const fullNovelSlug = id === "p" ? slug : `${slug}-${id}`;
  const fullChapterSlug = chapterId === "p" ? chapterSlug : `${chapterSlug}-${chapterId}`;

  const { data: chapter } = useSuspenseQuery({
    queryKey: ["chapter", fullNovelSlug, fullChapterSlug],
    queryFn: () => fetchChapter(fullNovelSlug, fullChapterSlug),
  }) as any;

  const { data: settings } = useSuspenseQuery({
    queryKey: ["settings"],
    queryFn: fetchSettings,
  }) as any;

  const chapters = chapter.post?.chapters || [];
  const currentIndex = chapters.findIndex((c: any) => c.slug === fullChapterSlug);
  const prevChapter = currentIndex > 0 ? chapters[currentIndex - 1] : null;
  const nextChapter = currentIndex < chapters.length - 1 ? chapters[currentIndex + 1] : null;

  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      setProgress(total > 0 ? Math.min(100, (h.scrollTop / total) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background pb-32">
      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 z-[100] bg-muted/20">
        <div 
          className="h-full bg-accent transition-all duration-300 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      <header className="container-prose pt-12 md:pt-20">
        <Link to="/novel/$id/$slug" params={{ id, slug }} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-accent transition-colors mb-10 group"><ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" /> Kembali ke Katalog</Link>
        
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-4">
          {chapter.post?.title}
        </div>
        <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-medium leading-[1.1] tracking-tight text-ink mb-12">
          Bab {chapter.order}: {chapter.title}
        </h1>
        
        <div className="mt-8 flex items-center justify-between text-xs sm:text-sm text-muted-foreground border-y border-border py-4">
          <div className="flex items-center gap-3">
            <span className="font-serif italic">Est. {Math.ceil(chapter.content.split(" ").length / 200)} Menit Membaca</span>
          </div>
        </div>
      </header>

      <div className="container-prose mt-16 md:mt-24">
          <article className="mx-auto">
            <div
              className="article-body ck-content prose prose-slate dark:prose-invert novel-content text-slate-700 dark:text-slate-300 select-none"
              onContextMenu={(e) => e.preventDefault()}
              suppressHydrationWarning dangerouslySetInnerHTML={{ __html: parseContent(chapter.content) }}
            />

            {/* Navigation between chapters */}
            <div className="mt-24 pt-12 border-t border-border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {prevChapter ? (
                  <Link
                    to="/novel/$id/$slug/chapter/$chapterId/$chapterSlug"
                    params={{
                      id,
                      slug,
                      chapterId: splitSlug(prevChapter.slug || "").id,
                      chapterSlug: splitSlug(prevChapter.slug || "").slug,
                    }}
                    className="flex flex-col gap-2 p-8 rounded-2xl border border-border hover:border-accent/30 hover:bg-accent/[0.02] transition-all group"
                  >
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                      <ChevronLeft size={12} className=" " /> Bab Sebelumnya
                    </span>
                    <span className="font-serif text-xl group-hover:text-accent transition-colors">
                      {prevChapter.title}
                    </span>
                  </Link>
                ) : (
                  <div className="hidden md:block" />
                )}

                {nextChapter ? (
                  <Link
                    to="/novel/$id/$slug/chapter/$chapterId/$chapterSlug"
                    params={{
                      id,
                      slug,
                      chapterId: splitSlug(nextChapter.slug || "").id,
                      chapterSlug: splitSlug(nextChapter.slug || "").slug,
                    }}
                    className="flex flex-col items-end gap-2 p-8 rounded-2xl border border-border hover:border-accent/30 hover:bg-accent/[0.02] transition-all group text-right"
                  >
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                      Bab Selanjutnya <ChevronRight size={12} className=" " />
                    </span>
                    <span className="font-serif text-xl group-hover:text-accent transition-colors">
                      {nextChapter.title}
                    </span>
                  </Link>
                ) : (
                  <div className="flex flex-col items-end gap-2 p-8 rounded-2xl border border-dashed border-border text-right opacity-50">
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      Tamat
                    </span>
                    <span className="font-serif text-xl">Akhir dari cerita</span>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-24 md:mt-32">
              <Suspense
                fallback={
                  <div className="h-32 flex items-center justify-center italic text-muted-foreground font-serif">
                    Memuat diskusi...
                  </div>
                }
              >
                <div className="my-12">
                  <AdSlot />
                  <CustomAdSlot />
                </div>
                <CommentSection postId={chapter.postId} chapterId={chapter.id} />
              </Suspense>
            </div>
          </article>
      </div>
    </div>
  );
}
